import React, { useState, useEffect } from 'react';
import TelegramFeedbackFields from '../components/TelegramFeedbackFields';
import LoginAccessStatus from '../components/LoginAccessStatus';
import { useLocation, useNavigate } from 'react-router-dom';
import apiService from '../services/apiService';
import './UserCreate.css';
import './UserCreateAdmin.css';

const UserEditAdmin = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, company, scrollY } = location.state || {};
    const backUrl = scrollY ? `/users?scrollY=${scrollY}` : '/users';
    const stores = (company?.units || []).flatMap(unit => unit.stores || []);

    const [fullname, setFullname] = useState(user?.fullname || '');
    const [username, setUsername] = useState(user?.username || '');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [role, setRole] = useState(user?.role || 'admin');
    const [selectedStoreIds, setSelectedStoreIds] = useState(user?.storeIds || []);
    const [submitting, setSubmitting] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [passwordMismatch, setPasswordMismatch] = useState(false);
    // Telegram: esta é a tela onde vivem diretor, gerente e supervisor — o público da feature.
    const [whatsappPhone, setWhatsappPhone] = useState(user?.whatsapp_phone || '');
    const [whatsappEnabled, setWhatsappEnabled] = useState(user?.whatsapp_enabled || false);
    const [telegramUsername, setTelegramUsername] = useState(user?.telegram_username || '');
    const [feedbackTelegramEnabled, setFeedbackTelegramEnabled] = useState(user?.feedback_telegram_enabled || false);
    const [feedbackTelegramShort, setFeedbackTelegramShort] = useState(
        user?.feedback_telegram_short !== undefined ? user.feedback_telegram_short : true);
    const [feedbackTelegramLong, setFeedbackTelegramLong] = useState(user?.feedback_telegram_long || false);

    // Empresas permitidas (core.admin_companies). É um acesso GLOBAL do admin, não um campo
    // desta empresa — por isso salva sozinho, num botão próprio, e não junto do formulário.
    const [acesso, setAcesso] = useState(null);          // resposta do GET; null = ainda não carregou
    const [empresasSel, setEmpresasSel] = useState([]);
    const [salvandoAcesso, setSalvandoAcesso] = useState(false);
    const [msgAcesso, setMsgAcesso] = useState({ type: '', text: '' });

    const toggleStore = (storeId) => {
        setSelectedStoreIds((prev) =>
            prev.includes(storeId)
                ? prev.filter((id) => id !== storeId)
                : [...prev, storeId]
        );
    };

    // O bloco só aparece para quem pode mexer: a API responde 403 a quem não é admin
    // irrestrito, e aí ele simplesmente não é renderizado (acesso segue null).
    useEffect(() => {
        if (!user?.user_id || user?.role !== 'admin') return;
        let vivo = true;
        (async () => {
            try {
                const data = await apiService.getAdminCompanyAccess(user.user_id);
                if (!vivo) return;
                setAcesso(data);
                setEmpresasSel(data.company_ids || []);
            } catch (err) {
                // 403 (não sou irrestrito) ou falha: o bloco fica fora da tela.
            }
        })();
        return () => { vivo = false; };
    }, [user]);

    const toggleEmpresa = (companyId) => {
        setMsgAcesso({ type: '', text: '' });
        setEmpresasSel((prev) =>
            prev.includes(companyId)
                ? prev.filter((id) => id !== companyId)
                : [...prev, companyId]
        );
    };

    const salvarAcesso = async () => {
        setMsgAcesso({ type: '', text: '' });
        try {
            setSalvandoAcesso(true);
            const data = await apiService.setAdminCompanyAccess(user.user_id, empresasSel);
            setAcesso((prev) => ({ ...prev, unrestricted: data.unrestricted, company_ids: data.company_ids }));
            setEmpresasSel(data.company_ids || []);
            setMsgAcesso({
                type: 'success',
                text: data.unrestricted
                    ? 'Acesso liberado para todas as empresas.'
                    : `Acesso limitado a ${data.company_ids.length} empresa(s).`,
            });
        } catch (err) {
            setMsgAcesso({ type: 'error', text: err.message || 'Erro ao salvar empresas permitidas.' });
        } finally {
            setSalvandoAcesso(false);
        }
    };

    if (!user) {
        return (
            <div className="user-create">
                <div className="user-create-header">
                    <button className="btn-back" onClick={() => navigate(backUrl)}>
                        &#8592; Voltar
                    </button>
                    <h1>Editar Administrador</h1>
                </div>
                <div className="alert alert-error">Usuário não encontrado.</div>
            </div>
        );
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage({ type: '', text: '' });

        if (!fullname.trim() || !username.trim()) {
            setMessage({ type: 'error', text: 'Nome completo e usuário são obrigatórios.' });
            return;
        }

        if (password && password !== confirmPassword) {
            setMessage({ type: 'error', text: 'As senhas não coincidem.' });
            return;
        }

        if (password && password.length < 4) {
            setMessage({ type: 'error', text: 'A senha deve ter pelo menos 4 caracteres.' });
            return;
        }

        if (selectedStoreIds.length === 0) {
            setMessage({ type: 'error', text: 'Selecione pelo menos uma loja.' });
            return;
        }

        try {
            setSubmitting(true);
            const payload = {
                user_id: user.user_id,
                company_id: company?.id,
                fullname: fullname.trim(),
                username: username.trim(),
                role,
                store_ids: selectedStoreIds.join(','),
            };
            if (password) {
                payload.password = password;
            }
            // '' apaga o telefone — é como a tela desfaz um cadastro errado.
            payload.whatsapp_phone = whatsappPhone.trim() || '';
            // Desligar nao apaga whatsapp_opt_in_at: o primeiro sim fica registrado.
            payload.whatsapp_enabled = whatsappEnabled && !!whatsappPhone.trim();
            payload.telegram_username = telegramUsername.trim() || null;
            payload.feedback_telegram_enabled = feedbackTelegramEnabled;
            payload.feedback_telegram_short = feedbackTelegramShort;
            payload.feedback_telegram_long = feedbackTelegramLong;

            const result = await apiService.updateUser(payload);

            if (result.success) {
                setMessage({ type: 'success', text: `Administrador "${fullname.trim()}" atualizado com sucesso!` });
                setPassword('');
                setConfirmPassword('');
            } else {
                const detail = JSON.stringify(result.details || '');
                if (detail.includes('duplicate key') || detail.includes('Username já existe')) {
                    setMessage({ type: 'error', text: 'Este nome de usuário já existe. Escolha outro.' });
                } else {
                    setMessage({ type: 'error', text: result.message || 'Erro ao atualizar administrador.' });
                }
            }
        } catch (err) {
            setMessage({ type: 'error', text: 'Erro ao atualizar administrador. Tente novamente.' });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="user-create">
            <div className="user-create-header">
                <button className="btn-back" onClick={() => navigate(backUrl)}>
                    &#8592; Voltar
                </button>
                <h1>Editar Administrador</h1>
            </div>

            {message.text && (
                <div className={`alert alert-${message.type}`}>
                    {message.text}
                </div>
            )}

            <form className="user-create-form" onSubmit={handleSubmit}>
                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="company">Empresa</label>
                        <input
                            id="company"
                            type="text"
                            value={company?.name || ''}
                            disabled
                            className="form-input"
                        />
                    </div>
                    <div className="form-group">
                        <label htmlFor="role">Perfil</label>
                        <select
                            id="role"
                            value={role}
                            onChange={(e) => setRole(e.target.value)}
                            className="form-input"
                        >
                            <option value="admin">Administrador</option>
                            <option value="director">Diretor</option>
                            <option value="supervisor">Supervisor</option>
                            <option value="manager">Gerente</option>
                        </select>
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="fullname">Nome Completo</label>
                        <input
                            id="fullname"
                            type="text"
                            value={fullname}
                            onChange={(e) => setFullname(e.target.value)}
                            className="form-input"
                            placeholder="Digite o nome completo"
                        />
                    </div>
                    <div className="form-group">
                        <label htmlFor="username">Usuário (login)</label>
                        <input
                            id="username"
                            type="text"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            className="form-input"
                            placeholder="Digite o nome de usuário"
                        />
                    </div>
                </div>

                <div className="form-row">
                    <LoginAccessStatus user={user} />
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="password">Nova Senha (deixe em branco para manter)</label>
                        <input
                            id="password"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="form-input"
                            placeholder="Nova senha (opcional)"
                        />
                    </div>
                    <div className="form-group">
                        <label htmlFor="confirmPassword">Confirmação da Nova Senha</label>
                        <input
                            id="confirmPassword"
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => {
                                setConfirmPassword(e.target.value);
                                setPasswordMismatch(false);
                            }}
                            onBlur={() => {
                                if (confirmPassword && confirmPassword !== password) {
                                    setPasswordMismatch(true);
                                }
                            }}
                            className={`form-input ${passwordMismatch ? 'form-input-error' : ''}`}
                            placeholder="Confirme a nova senha"
                        />
                        {passwordMismatch && (
                            <span className="field-error">As senhas não coincidem.</span>
                        )}
                    </div>
                </div>

                <div className="form-group stores-selection">
                    <label>Lojas com Acesso</label>
                    <div className="stores-checkboxes">
                        {stores.map((store) => (
                            <label key={store.id} className="store-checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={selectedStoreIds.includes(store.id)}
                                    onChange={() => toggleStore(store.id)}
                                />
                                {store.name}
                            </label>
                        ))}
                    </div>
                </div>

                <div className="form-row">

                    <div className="form-group">

                        <label htmlFor="whatsappPhone">WhatsApp <span className="field-optional">(opcional)</span></label>

                        <input

                            id="whatsappPhone"

                            type="tel"

                            value={whatsappPhone}

                            onChange={(e) => setWhatsappPhone(e.target.value)}

                            className="form-input"

                            placeholder="(19) 98113-1421"

                        />

                        <small className="field-hint">

                            Com DDD. Apagar o campo remove o número.

                        </small>

                    </div>
                    <div className="form-group">
                        <label htmlFor="whatsappEnabled">Receber relatórios por WhatsApp?</label>
                        <select
                            id="whatsappEnabled"
                            value={whatsappEnabled ? 'true' : 'false'}
                            onChange={(e) => setWhatsappEnabled(e.target.value === 'true')}
                            className="form-input"
                            disabled={!whatsappPhone.trim()}
                        >
                            <option value="false">Não</option>
                            <option value="true">Sim</option>
                        </select>
                        <small className="field-hint">
                            {whatsappPhone.trim()
                                ? 'Ao marcar Sim você registra o consentimento da pessoa. Desligar não apaga a data do primeiro sim.'
                                : 'Preencha o telefone para poder ligar o envio automático.'}
                        </small>
                    </div>

                </div>


                <TelegramFeedbackFields
                    userId={user?.user_id}
                    role={role}
                    username={telegramUsername}
                    onUsernameChange={setTelegramUsername}
                    enabled={feedbackTelegramEnabled}
                    onEnabledChange={setFeedbackTelegramEnabled}
                    short={feedbackTelegramShort}
                    onShortChange={setFeedbackTelegramShort}
                    long={feedbackTelegramLong}
                    onLongChange={setFeedbackTelegramLong}
                    linkedInicial={user?.telegram_linked}
                    linkedAtInicial={user?.telegram_linked_at}
                />
                <div className="form-actions">
                    <button
                        type="button"
                        className="btn btn-cancel"
                        onClick={() => navigate(backUrl)}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="btn btn-submit"
                        disabled={submitting}
                    >
                        {submitting ? 'Salvando...' : 'Salvar Alterações'}
                    </button>
                </div>
            </form>

            {acesso && (
                <div className="user-create-form">
                    <div className="form-group stores-selection">
                        <label>Empresas Permitidas no Painel</label>
                        <p className="field-hint">
                            Limita quais empresas este administrador consegue escolher nas telas do painel.
                            Nenhuma marcada = acesso a todas as empresas.
                        </p>

                        {acesso.is_self ? (
                            <div className="alert alert-info">
                                Você não pode alterar as suas próprias empresas permitidas. Peça a outro
                                administrador com acesso total.
                            </div>
                        ) : (
                            <>
                                <div className="stores-checkboxes">
                                    {(acesso.available_companies || []).map((empresa) => (
                                        <label key={empresa.company_id} className="store-checkbox-label">
                                            <input
                                                type="checkbox"
                                                checked={empresasSel.includes(empresa.company_id)}
                                                onChange={() => toggleEmpresa(empresa.company_id)}
                                            />
                                            {empresa.name}
                                        </label>
                                    ))}
                                </div>

                                {msgAcesso.text && (
                                    <div className={`alert alert-${msgAcesso.type}`}>{msgAcesso.text}</div>
                                )}

                                <div className="form-actions">
                                    <button
                                        type="button"
                                        className="btn btn-submit"
                                        onClick={salvarAcesso}
                                        disabled={salvandoAcesso}
                                    >
                                        {salvandoAcesso ? 'Salvando...' : 'Salvar Empresas Permitidas'}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserEditAdmin;